import { EditorSelection, type ChangeSpec, type Line } from "@codemirror/state";
import { type Command, type KeyBinding } from "@codemirror/view";

/**
 * Formatting shortcuts for the markdown editor (Fase 2).
 *
 * Every command edits the plain `.md` text directly — inserting/removing the
 * same markers a person would type (`**`, `#`, `- `…). Nothing here introduces
 * a non-standard format, so the file stays fully portable to Obsidian & co.
 * The Live Preview layer then re-renders the result instantly.
 */

/** Wrap the selection in `marker`, or unwrap it if it's already wrapped. */
function toggleWrap(marker: string): Command {
  return (view) => {
    const { state } = view;
    const len = marker.length;
    const tr = state.changeByRange((range) => {
      const { from, to } = range;

      // Markers sitting just outside the selection → strip them.
      const before = state.sliceDoc(Math.max(0, from - len), from);
      const after = state.sliceDoc(to, to + len);
      if (before === marker && after === marker) {
        return {
          changes: [
            { from: from - len, to: from },
            { from: to, to: to + len },
          ],
          range: EditorSelection.range(from - len, to - len),
        };
      }

      // Markers included inside the selection → strip them.
      const inner = state.sliceDoc(from, to);
      if (inner.length >= len * 2 && inner.startsWith(marker) && inner.endsWith(marker)) {
        return {
          changes: [
            { from, to: from + len },
            { from: to - len, to },
          ],
          range: EditorSelection.range(from, to - len * 2),
        };
      }

      // Otherwise wrap.
      return {
        changes: [
          { from, insert: marker },
          { from: to, insert: marker },
        ],
        range:
          from === to
            ? EditorSelection.cursor(from + len)
            : EditorSelection.range(from + len, to + len),
      };
    });
    view.dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input.format" }));
    return true;
  };
}

/** Set every touched line to heading `level` (0 = clear); toggles off if same. */
function setHeading(level: number): Command {
  return (view) => {
    const { state } = view;
    const changes: ChangeSpec[] = [];
    const seen = new Set<number>();
    for (const range of state.selection.ranges) {
      const startLine = state.doc.lineAt(range.from).number;
      const endLine = state.doc.lineAt(range.to).number;
      for (let n = startLine; n <= endLine; n++) {
        if (seen.has(n)) continue;
        seen.add(n);
        const line = state.doc.line(n);
        const m = /^(#{1,6})\s+/.exec(line.text);
        const currentLen = m ? m[0].length : 0;
        const currentLevel = m ? m[1].length : 0;
        const prefix = level === 0 || currentLevel === level ? "" : "#".repeat(level) + " ";
        changes.push({ from: line.from, to: line.from + currentLen, insert: prefix });
      }
    }
    if (!changes.length) return false;
    view.dispatch(state.update({ changes, userEvent: "input.format" }));
    return true;
  };
}

/** Toggle a `- ` bullet on every touched line (off if all already bulleted). */
const toggleBulletList: Command = (view) => {
  const { state } = view;
  const seen = new Set<number>();
  const lines: Line[] = [];
  for (const range of state.selection.ranges) {
    const startLine = state.doc.lineAt(range.from).number;
    const endLine = state.doc.lineAt(range.to).number;
    for (let n = startLine; n <= endLine; n++) {
      if (seen.has(n)) continue;
      seen.add(n);
      lines.push(state.doc.line(n));
    }
  }
  if (!lines.length) return false;

  const allBulleted = lines.every((l) => /^\s*[-*+]\s+/.test(l.text));
  const changes: ChangeSpec[] = lines.map((line) => {
    if (allBulleted) {
      const m = /^(\s*)[-*+]\s+/.exec(line.text)!;
      return { from: line.from + m[1].length, to: line.from + m[0].length, insert: "" };
    }
    const indent = /^\s*/.exec(line.text)![0].length;
    return { from: line.from + indent, insert: "- " };
  });
  view.dispatch(state.update({ changes, userEvent: "input.format" }));
  return true;
};

export const formattingKeymap: KeyBinding[] = [
  { key: "Mod-b", run: toggleWrap("**"), preventDefault: true },
  { key: "Mod-i", run: toggleWrap("*"), preventDefault: true },
  { key: "Mod-e", run: toggleWrap("`"), preventDefault: true },
  { key: "Mod-Shift-s", run: toggleWrap("~~"), preventDefault: true },
  { key: "Mod-Shift-8", run: toggleBulletList, preventDefault: true },
  { key: "Mod-Alt-1", run: setHeading(1), preventDefault: true },
  { key: "Mod-Alt-2", run: setHeading(2), preventDefault: true },
  { key: "Mod-Alt-3", run: setHeading(3), preventDefault: true },
  { key: "Mod-Alt-4", run: setHeading(4), preventDefault: true },
  { key: "Mod-Alt-5", run: setHeading(5), preventDefault: true },
  { key: "Mod-Alt-6", run: setHeading(6), preventDefault: true },
  { key: "Mod-Alt-0", run: setHeading(0), preventDefault: true },
];
